// Hideo - video formats (e.g. "Super Mario 64", "Trivia"): a recurring series a video belongs to (logic without UI).
// The UI calls them "Format"; the data keeps the old names data.series and video.seriesId (never renamed). Each format
// belongs to a project: name + colour (+ optional icon), one format per video.
// Used by the plan filter bar (src/components/PedFilters.vue), the Board and Published cards (seriesBadge), the video
// dialog's format field (src/components/video/FormatPicker.vue, createFormat) (nextSeriesColor). Managed on the Formats page (FormatDialog.vue)
// (src/features/settings.ts).
import { SERIES_COLORS, colorDot, safeColor } from '../utils/color'
import type { ProfileData, Series } from '../data/types'
import { esc } from '../utils/html'
import { readableHex } from './theme'
import { lookIconHtml } from './looks'
import { t } from '../i18n'

/** Format filter value for "No format" ('' = all, otherwise 'n:<lower-case name>') */
export const FORMAT_NONE = 'none'
/** Longest format name */
export const FORMAT_NAME_MAX = 40

/** The format with this id, or null */
export const seriesOf = (d: Pick<ProfileData, 'series'>, id?: string | null): Series | null =>
  id ? (d.series || []).find(s => s.id === id) || null : null

/** Filter key of a format: its name, case-insensitive (same-name formats of different projects are one filter) */
export const seriesKey = (s: Pick<Series, 'name'>): string => 'n:' + s.name.trim().toLowerCase()

/** First colour not used by any format (then the colours start again) */
export function nextSeriesColor(d: Pick<ProfileData, 'series'>): string {
  const used = (d.series || []).map(s => s.color)
  return SERIES_COLORS.find(c => !used.includes(c)) || SERIES_COLORS[(d.series || []).length % SERIES_COLORS.length]
}

/** Adds a format to a project (name trimmed to 40 characters), or gives the one with the same name (case-insensitive).
 *  id '' = empty name; created = the data changed and must be saved */
export function createFormat(d: ProfileData, name: string, color: string, projectId: string, now = Date.now()): { id: string; created: boolean } {
  name = String(name || '').trim().slice(0, FORMAT_NAME_MAX)
  if (!name) return { id: '', created: false }
  const same = d.series.find(s => s.projectId === projectId && s.name.toLowerCase() === name.toLowerCase())
  if (same) return { id: same.id, created: false }
  const id = 'se_' + now
  d.series.push({ id, name, color: safeColor(color), projectId } as Series)   // no icon, like before (normalizeData adds '')
  return { id, created: true }
}

/** One filter pill: formats with the same name in the visible projects (colour and icon of the first) */
export interface FormatGroup {
  key: string
  name: string
  color: string
  icon: string
  ids: string[]
}
export function formatGroups(series: Series[]): FormatGroup[] {
  const groups: FormatGroup[] = []
  series.forEach(s => {
    const key = seriesKey(s), g = groups.find(x => x.key === key)
    if (g) g.ids.push(s.id)
    else groups.push({ key, name: s.name, color: s.color, icon: s.icon, ids: [s.id] })
  })
  return groups
}

/** Format of the active filter in a project (a new video there starts with it): '' without a format filter */
export function formatOfFilter(d: Pick<ProfileData, 'series'>, filter: string, projectId: string): string {
  if (!filter || filter === FORMAT_NONE) return ''
  return ((d.series || []).find(s => s.projectId === projectId && seriesKey(s) === filter) || { id: '' }).id
}

// ===== Marks drawn as HTML (Board and Published cards; Vue templates use FormatMark.vue) =====
/** Mark of a format: its icon in its colour, otherwise the coloured dot; HTML */
export const seriesMark = (s: Pick<Series, 'color' | 'icon'>, cls = 'w-2.5 h-2.5', ico = 'text-[11px]', img = 'w-3.5 h-3.5'): string =>
  (s.icon ? lookIconHtml({ icon: s.icon, color: safeColor(s.color) }, ico, img) || colorDot(s.color, cls) : colorDot(s.color, cls))
/** Coloured label of a format, '' without one; HTML (the name is escaped). link: the label opens the format's card
 *  (data-format = its id, read by the listener of the element that holds the HTML: openFormatCard) */
export const seriesBadge = (s: Series | null, link = false): string => (s
  ? `<span class="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-0.5 rounded-md font-bold border max-w-full${link ? ' cursor-pointer hover:brightness-125' : ''}"${link ? ` data-format="${esc(s.id)}" role="button" title="${esc(t('Open the format card'))}"` : ''} style="color:${readableHex(safeColor(s.color))};border-color:${safeColor(s.color)}55;background:${safeColor(s.color)}1a">${seriesMark(s, 'w-2 h-2', 'text-[10px]', 'w-3 h-3')}<span class="truncate">${esc(s.name)}</span></span>`
  : '')
